// Single registration point for i18n namespaces and their locales (ADR 0010). This file is a
// hotspot: a new namespace is added here and in `catalogs/<namespace>/`, and nowhere else.
// Everything downstream (generated key types, `t()`, the catalogue lint) derives from it.

/**
 * One namespace per consumer. Adding a value here without adding `catalogs/<namespace>/nl.json`
 * is a compile error in `src/catalogs.ts`, because the generated key map no longer covers it.
 */
export const NAMESPACES = [
  'console',
  'invoice',
  'notify',
  'pdf',
  'requirements',
  'rules',
  'sign',
] as const

export type Namespace = (typeof NAMESPACES)[number]

/** Locales every namespace ships. `nl` is the source; `en` and `de` are kept in sync by the lint. */
export const LOCALES = ['nl', 'en', 'de'] as const

export type Locale = (typeof LOCALES)[number]

/** The language the catalogues are authored in and the one the key types are generated from. */
export const SOURCE_LOCALE = 'nl' satisfies Locale

/**
 * Locales that only a specific namespace carries. The CMR consignment note has official French box
 * labels, so `pdf` ships `fr.json`; `sign` follows it because the inspection page (M2-09) renders
 * those very labels around its own chrome, and an inspector who picks French must get the whole
 * page in French rather than French boxes in a Dutch frame.
 */
export const EXTRA_LOCALES = { pdf: ['fr'], sign: ['fr'] } as const

type NamespaceWithExtraLocales = keyof typeof EXTRA_LOCALES

/** Widened view of {@link EXTRA_LOCALES} for lookups by a namespace that may not have any. */
const extraLocalesByNamespace: Partial<Record<Namespace, readonly CatalogLocale[]>> = EXTRA_LOCALES

/** Every locale that may appear as a catalogue file, across all namespaces. */
export type CatalogLocale = Locale | (typeof EXTRA_LOCALES)[NamespaceWithExtraLocales][number]

/** The locales a single namespace can be asked for: `pdf` adds `fr`, the rest stay on nl/en/de. */
export type LocaleFor<N extends Namespace> = N extends NamespaceWithExtraLocales
  ? Locale | (typeof EXTRA_LOCALES)[N][number]
  : Locale

/** The locales `namespace` has a catalogue for, source locale first. */
export function localesFor<N extends Namespace>(namespace: N): readonly LocaleFor<N>[] {
  const locales: readonly CatalogLocale[] = [
    ...LOCALES,
    ...(extraLocalesByNamespace[namespace] ?? []),
  ]
  // TypeScript cannot relate a value to a deferred conditional type; the elements come straight
  // from LOCALES plus the extras registered for this namespace.
  return locales as unknown as readonly LocaleFor<N>[]
}

export function isNamespace(value: unknown): value is Namespace {
  return typeof value === 'string' && (NAMESPACES as readonly string[]).includes(value)
}

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value)
}
